import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError, fmtDateTime, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, CheckCircle2, Clock, Send, FileText, Play } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PhotoAttach from "../components/PhotoAttach";

const STATUS_LABEL = {
    OPEN: "Open", PM_INFORMED: "PM informed", HO_CLASSIFIED: "HO classified",
    QUOTATION_SENT: "Quotation sent", CLIENT_APPROVED: "Client approved",
    WORK_IN_PROGRESS: "Work in progress", CLOSED: "Closed",
};
const STATUS_ORDER = ["OPEN", "PM_INFORMED", "HO_CLASSIFIED", "QUOTATION_SENT", "CLIENT_APPROVED", "WORK_IN_PROGRESS", "CLOSED"];

export default function SRDetailPage() {
    const { srId } = useParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const [sr, setSr] = useState(null);
    const [loading, setLoading] = useState(true);

    const isPM = user?.role === "PROJECT_MANAGER";
    const isHO = user?.role === "HO_ADMIN";

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/service-requests/${srId}`); setSr(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [srId]);
    useEffect(() => { load(); }, [load]);

    async function act(path, body) {
        try {
            await api.post(`/service-requests/${srId}${path}`, body || {});
            toast.success("Updated");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    if (loading || !sr) return <div className="mx-auto max-w-3xl px-4 py-8" data-testid="sr-detail-loading"><div className="h-40 rounded-xl bg-white animate-pulse" /></div>;

    const cls = sr.classification;
    const canAcknowledge = sr.status === "OPEN" && (isPM || isHO);
    const canClassify = isHO && ["OPEN", "PM_INFORMED", "HO_CLASSIFIED"].includes(sr.status);
    const canQuote = isHO && cls === "CHARGEABLE" && ["HO_CLASSIFIED", "QUOTATION_SENT"].includes(sr.status);
    const canApprove = cls === "CHARGEABLE" && sr.status === "QUOTATION_SENT";
    const canStartNonCharge = cls === "NON_CHARGEABLE" && sr.status === "HO_CLASSIFIED";
    const canStartCharge = cls === "CHARGEABLE" && sr.status === "CLIENT_APPROVED";
    const canCloseInProg = sr.status === "WORK_IN_PROGRESS";
    const canCloseOutOfScope = cls === "OUT_OF_SCOPE" && sr.status === "HO_CLASSIFIED";

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/service-requests" className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3" data-testid="sr-back"><ChevronLeft className="w-3 h-3" /> All requests</Link>

            <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Service Request</div>
                        <h1 className="font-display text-xl font-bold text-[#1E3355] mt-1">{sr.site_name} · {sr.category}</h1>
                        <div className="text-xs text-gray-500 mt-1">Raised {fmtDateTime(sr.created_at)} by {sr.created_by_name}</div>
                    </div>
                    <div className="text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#F4F6F9] text-[#1E3355] border border-gray-200" data-testid="sr-detail-status">{STATUS_LABEL[sr.status]}</span>
                        {sr.is_breached && <div className="mt-1"><span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#E02B2B] bg-red-50 border border-red-200 rounded px-2 py-0.5" data-testid="sr-detail-breach"><AlertTriangle className="w-3 h-3" /> BREACHED</span></div>}
                    </div>
                </div>
                <div className="mt-4 text-sm text-gray-800 whitespace-pre-wrap">{sr.description}</div>
                <div className="mt-3 text-[11px] text-gray-500">SLA check: <span className="font-semibold text-[#1E3355]">{sr.sla_check}</span>{sr.asset_item_name && <> · Asset: <span className="font-semibold">{sr.asset_item_name}</span> (SLA {sr.asset_sla_hours}h)</>}</div>

                <div className="mt-5">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Progress</div>
                    <ol className="grid grid-cols-7 gap-1 text-[10px]" data-testid="sr-progress-timeline">
                        {STATUS_ORDER.map((s) => {
                            const done = STATUS_ORDER.indexOf(sr.status) >= STATUS_ORDER.indexOf(s);
                            return (<li key={s} className="text-center">
                                <div className={`h-1.5 rounded-full ${done ? "bg-[#1E3355]" : "bg-gray-200"}`} />
                                <div className={`mt-1 truncate ${done ? "text-[#1E3355] font-semibold" : "text-gray-400"}`}>{STATUS_LABEL[s]}</div>
                            </li>);
                        })}
                    </ol>
                </div>
            </div>

            {/* Photos */}
            <PhotoSection title="Before" tag="before" siteId={sr.site_id} srId={sr.id} locked={sr.status === "CLOSED"} />
            {(canApprove || sr.client_approval_ref) && (
                <PhotoSection title="Client approval file" tag="approval" siteId={sr.site_id} srId={sr.id} locked={sr.status === "CLOSED"} />
            )}
            <PhotoSection title="After" tag="after" siteId={sr.site_id} srId={sr.id} locked={sr.status === "CLOSED"} />

            {/* Actions */}
            <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5" data-testid="sr-actions">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Actions</div>
                {sr.status === "CLOSED" ? (
                    <div className="text-sm text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Closed {sr.closed_at ? `on ${fmtDate(sr.closed_at)}` : ""} — resolution time {sr.resolution_minutes ? `${Math.round(sr.resolution_minutes / 60)}h` : "—"}</div>
                ) : (
                    <div className="space-y-2">
                        {canAcknowledge && <ActionButton onClick={() => act("/acknowledge")} testid="sr-acknowledge-btn" label="Acknowledge (PM informed)" icon={<Send className="w-4 h-4" />} />}
                        {canClassify && <ClassifyAction sr={sr} onDone={load} />}
                        {canQuote && <QuotationAction sr={sr} onDone={load} />}
                        {canApprove && <ApprovalAction sr={sr} onDone={load} />}
                        {(canStartNonCharge || canStartCharge) && <ActionButton onClick={() => act("/start-work")} testid="sr-start-work-btn" label="Start work" icon={<Play className="w-4 h-4" />} />}
                        {(canCloseInProg || canCloseOutOfScope) && <CloseAction onDone={load} srId={sr.id} />}
                    </div>
                )}
            </div>

            {sr.classification && (
                <div className="mt-4 text-[11px] text-gray-500">
                    Classification: <span className="font-semibold text-[#1E3355]">{sr.classification}</span>{sr.classification_note && ` — ${sr.classification_note}`}
                    {sr.acknowledged_at && <> · Acknowledged {fmtDateTime(sr.acknowledged_at)} by {sr.acknowledged_by_name} (response {Math.round((sr.response_minutes || 0) / 60)}h)</>}
                    {sr.quotation_sent_at && <> · Quoted ₹{sr.quotation_amount?.toLocaleString("en-IN") || "—"}</>}
                    {sr.client_approval_ref && <> · Approved: {sr.client_approval_ref}</>}
                </div>
            )}
        </div>
    );
}

function ActionButton({ onClick, label, icon, testid, variant = "primary" }) {
    const cls = variant === "primary" ? "bg-[#1E3355] hover:bg-[#15243C] text-white" : "bg-white border border-gray-200 text-[#1E3355] hover:bg-gray-50";
    return <button onClick={onClick} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold ${cls}`} data-testid={testid}>{icon} {label}</button>;
}

function ClassifyAction({ sr, onDone }) {
    const [c, setC] = useState(sr.classification || "NON_CHARGEABLE");
    const [note, setNote] = useState("");
    async function submit() {
        try { await api.post(`/service-requests/${sr.id}/classify`, { classification: c, note }); toast.success("Classified"); onDone(); }
        catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="rounded-md border border-gray-200 p-3">
            <div className="text-sm font-medium text-[#1E3355] mb-2">HO classification</div>
            <div className="flex flex-wrap gap-2 mb-2">
                {[["NON_CHARGEABLE", "Non-chargeable"], ["CHARGEABLE", "Chargeable"], ["OUT_OF_SCOPE", "Out of scope"]].map(([k, l]) => (
                    <button key={k} onClick={() => setC(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${c === k ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200"}`} data-testid={`classify-${k}`}>{l}</button>
                ))}
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" className="w-full text-sm rounded border border-gray-200 px-2 py-1.5 mb-2" />
            <button onClick={submit} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="classify-submit">Save classification</button>
        </div>
    );
}

function QuotationAction({ sr, onDone }) {
    const [amount, setAmount] = useState(sr.quotation_amount || "");
    const [note, setNote] = useState(sr.quotation_note || "");
    async function submit() {
        try { await api.post(`/service-requests/${sr.id}/quotation`, { amount: amount ? Number(amount) : null, note }); toast.success("Quotation recorded"); onDone(); }
        catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="rounded-md border border-gray-200 p-3">
            <div className="text-sm font-medium text-[#1E3355] mb-2">Send quotation</div>
            <div className="flex gap-2 mb-2">
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount ₹" className="w-32 text-sm rounded border border-gray-200 px-2 py-1.5" data-testid="quote-amount" />
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="flex-1 text-sm rounded border border-gray-200 px-2 py-1.5" data-testid="quote-note" />
            </div>
            <button onClick={submit} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="quote-submit"><FileText className="w-4 h-4" /> Record quotation</button>
        </div>
    );
}

function ApprovalAction({ sr, onDone }) {
    const [ref, setRef] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    async function submit() {
        if (!ref) { toast.error("Approval reference required"); return; }
        try {
            await api.post(`/service-requests/${sr.id}/approval`, { reference: ref, approval_date: date });
            toast.success("Approval recorded"); onDone();
        } catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="text-sm font-medium text-[#1E3355] mb-2">Record client approval</div>
            <div className="text-[11px] text-amber-800 mb-2">Upload the client approval file above as a photo with tag <b>approval</b>. Without it, this action is blocked.</div>
            <div className="flex flex-wrap gap-2 mb-2">
                <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Approval reference (email/PO)" className="flex-1 min-w-[200px] text-sm rounded border border-gray-200 px-2 py-1.5" data-testid="approval-ref" />
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="text-sm rounded border border-gray-200 px-2 py-1.5" data-testid="approval-date" />
            </div>
            <button onClick={submit} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="approval-submit"><CheckCircle2 className="w-4 h-4" /> Record approval</button>
        </div>
    );
}

function CloseAction({ srId, onDone }) {
    const [note, setNote] = useState("");
    async function submit() {
        if (!note.trim()) { toast.error("Closure note required"); return; }
        try { await api.post(`/service-requests/${srId}/close`, { closure_note: note }); toast.success("Closed"); onDone(); }
        catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-sm font-medium text-[#1E3355] mb-2">Close request</div>
            <div className="text-[11px] text-emerald-800 mb-2">Attach at least one <b>after</b> photo above first.</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Closure / resolution note" className="w-full text-sm rounded border border-gray-200 px-2 py-1.5 mb-2" data-testid="close-note" />
            <button onClick={submit} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="close-submit"><CheckCircle2 className="w-4 h-4" /> Confirm close</button>
        </div>
    );
}

function PhotoSection({ title, tag, siteId, srId, locked }) {
    return (
        <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`sr-photos-${tag}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{title}</div>
            <PhotoAttach parentType="service_request" parentId={srId} siteId={siteId} tag={tag} disabled={locked} testIdPrefix={`sr-${tag}-photo`} />
        </div>
    );
}
